<template>
  <section id="gallery" class="py-12" style="background-color: #fdfaff;">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Section Header -->
      <div class="mb-8 text-center">
        <h2 class="text-2xl font-semibold text-gray-900 mb-2">แกลเลอรี่</h2>
        <p class="text-gray-600">รูปภาพและสื่อต่างๆ</p>
      </div>

      <!-- Simple Category Filter -->
      <div v-if="!isLoading && !error && galleryItems.length > 0" class="mb-6">
        <!-- Type Filter -->
        <div class="flex flex-wrap justify-center gap-2 mb-4">
          <button 
            @click="selectedType = 'all'"
            :class="selectedType === 'all' ? 'text-gray-900 border-gray-900' : 'text-gray-500 border-gray-300 hover:text-gray-700'"
            class="px-3 py-1 text-sm border-b-2 transition-colors">
            ทั้งหมด
          </button>
          <button 
            v-if="availableTypes.includes('image')"
            @click="selectedType = 'image'"
            :class="selectedType === 'image' ? 'text-gray-900 border-gray-900' : 'text-gray-500 border-gray-300 hover:text-gray-700'"
            class="px-3 py-1 text-sm border-b-2 transition-colors">
            รูปภาพ
          </button>
          <button 
            v-if="availableTypes.includes('video')"
            @click="selectedType = 'video'"
            :class="selectedType === 'video' ? 'text-gray-900 border-gray-900' : 'text-gray-500 border-gray-300 hover:text-gray-700'"
            class="px-3 py-1 text-sm border-b-2 transition-colors">
            วีดีโอ
          </button>
          <button 
            v-if="availableTypes.includes('document')"
            @click="selectedType = 'document'"
            :class="selectedType === 'document' ? 'text-gray-900 border-gray-900' : 'text-gray-500 border-gray-300 hover:text-gray-700'"
            class="px-3 py-1 text-sm border-b-2 transition-colors">
            เอกสาร
          </button>
        </div>

        <!-- Album Filter -->
        <div v-if="availableAlbums.length > 1" class="flex flex-wrap justify-center gap-2">
          <button 
            @click="selectedAlbum = 'all'"
            :class="selectedAlbum === 'all' ? 'text-gray-900 border-gray-900' : 'text-gray-500 border-gray-300 hover:text-gray-700'"
            class="px-3 py-1 text-sm border-b-2 transition-colors">
            อัลบั้มทั้งหมด
          </button>
          <button 
            v-for="album in availableAlbums.slice(0, 4)" 
            :key="album"
            @click="selectedAlbum = album"
            :class="selectedAlbum === album ? 'text-gray-900 border-gray-900' : 'text-gray-500 border-gray-300 hover:text-gray-700'"
            class="px-3 py-1 text-sm border-b-2 transition-colors">
            {{ album }}
          </button>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="isLoading" class="flex justify-center items-center py-12">
        <div class="text-center">
          <div class="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-gray-600 mx-auto mb-2"></div>
          <p class="text-sm text-gray-500">กำลังโหลด...</p>
        </div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="text-center py-12">
        <p class="text-gray-500 mb-3">{{ error }}</p>
        <button
          @click="fetchGalleryData"
          class="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 transition-colors"
        >
          ลองใหม่
        </button>
      </div>

      <!-- Gallery Grid -->
      <div v-if="!isLoading && !error && filteredItems.length > 0">
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <div
            v-for="item in displayedItems"
            :key="item._id || item.id"
            class="bg-white border border-gray-200 rounded-lg overflow-hidden hover:border-gray-300 transition-colors cursor-pointer shadow-sm"
            @click="openLightbox(item)"
          >
            <!-- Image -->
            <div class="relative h-40 bg-gray-100">
              <img
                v-if="item.type === 'image'"
                :src="item.thumbnailUrl || item.fileUrl || item.url"
                :alt="item.name || item.title"
                class="w-full h-full object-cover"
                loading="lazy"
                @error="handleImageError($event)"
              />
              <div v-else-if="item.type === 'video'" class="flex items-center justify-center h-full bg-gray-900">
                <i class="fas fa-play-circle text-white text-3xl"></i>
              </div>
              <div v-else class="flex items-center justify-center h-full">
                <i class="fas fa-file-alt text-gray-400 text-2xl"></i>
              </div>
              
              <!-- Type Badge -->
              <div v-if="item.type !== 'image'" class="absolute top-3 right-3">
                <span class="text-xs text-white bg-black bg-opacity-60 px-2 py-1 rounded">
                  {{ getTypeLabel(item.type) }}
                </span>
              </div>
            </div>
            
            <!-- Content -->
            <div class="p-4">
              <h3 class="font-medium text-gray-900 mb-2 line-clamp-2">
                {{ item.name || item.title || 'ไม่มีชื่อ' }}
              </h3>
              
              <div class="flex items-center justify-between">
                <span v-if="item.album" class="text-xs text-gray-500">
                  {{ item.album }}
                </span>
                <span class="text-sm text-gray-700 hover:text-gray-900">
                  ดู →
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else-if="!isLoading && !error" class="text-center py-12">
        <p class="text-gray-500">ยังไม่มีรูปภาพในแกลเลอรี่</p>
      </div>
    </div>

    <!-- Lightbox Modal -->
    <div v-if="lightbox.show" 
         class="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4"
         @click="closeLightbox">
      <div class="relative max-w-4xl max-h-full" @click.stop>
        <!-- Close Button -->
        <button @click="closeLightbox" 
                class="absolute -top-12 right-0 text-white text-2xl hover:text-gray-300 transition-colors">
          <i class="fas fa-times"></i>
        </button>

        <!-- Navigation Buttons -->
        <button v-if="lightbox.currentIndex > 0"
                @click="previousImage"
                class="absolute left-4 top-1/2 transform -translate-y-1/2 text-white text-3xl hover:text-gray-300 transition-colors">
          <i class="fas fa-chevron-left"></i>
        </button>
        
        <button v-if="lightbox.currentIndex < filteredItems.length - 1"
                @click="nextImage"
                class="absolute right-4 top-1/2 transform -translate-y-1/2 text-white text-3xl hover:text-gray-300 transition-colors">
          <i class="fas fa-chevron-right"></i>
        </button>

        <!-- Image/Video Content -->
        <div class="bg-white rounded-lg overflow-hidden shadow-2xl">
          <img v-if="lightbox.item && lightbox.item.type === 'image'" 
               :src="lightbox.item.fileUrl || lightbox.item.url" 
               :alt="lightbox.item.name || lightbox.item.title"
               class="max-w-full max-h-[80vh] object-contain">
          
          <div v-else-if="lightbox.item && lightbox.item.type === 'video'" 
               class="w-full max-w-4xl">
            <video controls class="w-full max-h-[80vh]">
              <source :src="lightbox.item.fileUrl || lightbox.item.url" type="video/mp4">
              Your browser does not support the video tag.
            </video>
          </div>
          
          <div v-else-if="lightbox.item && lightbox.item.type === 'document'"
               class="p-8 text-center">
            <i class="fas fa-file-alt text-6xl text-blue-600 mb-4"></i>
            <p class="text-lg font-semibold mb-2">{{ lightbox.item.name || lightbox.item.title }}</p>
            <a :href="lightbox.item.fileUrl || lightbox.item.url" 
               target="_blank"
               class="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors">
              <i class="fas fa-download mr-2"></i>ดาวน์โหลด
            </a>
          </div>

          <!-- Image Info -->
          <div v-if="lightbox.item" class="p-4 border-t">
            <h4 class="font-semibold text-lg mb-2">{{ lightbox.item.name || lightbox.item.title }}</h4>
            <div class="flex items-center justify-between text-sm text-gray-600">
              <span v-if="lightbox.item.album">อัลบั้ม: {{ lightbox.item.album }}</span>
              <span>{{ lightbox.currentIndex + 1 }} / {{ filteredItems.length }}</span>
            </div>
            <p v-if="lightbox.item.description" class="text-gray-700 mt-2">{{ lightbox.item.description }}</p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useNuxtApp } from '#app'

// Props
const props = defineProps({
  clusterId: {
    type: String,
    required: true
  }
})

// Composables
const { $DSS } = useNuxtApp()

// State
const galleryItems = ref([])
const isLoading = ref(true)
const error = ref(null)

// Filter states
const selectedType = ref('all')
const selectedAlbum = ref('all')

// Lightbox state
const lightbox = ref({
  show: false,
  item: null,
  currentIndex: 0
})

// Computed
const filteredItems = computed(() => {
  let filtered = [...galleryItems.value]
  
  // Filter by type
  if (selectedType.value !== 'all') {
    filtered = filtered.filter(item => item.type === selectedType.value)
  }
  
  // Filter by album
  if (selectedAlbum.value !== 'all') {
    filtered = filtered.filter(item => item.album === selectedAlbum.value)
  }
  
  return filtered
})

const displayedItems = computed(() => {
  // แสดงแค่ 9 รายการล่าสุด เหมือน section อื่นๆ
  return filteredItems.value.slice(0, 9)
})

const availableAlbums = computed(() => {
  const albums = [...new Set(galleryItems.value.map(item => item.album).filter(album => album))]
  return albums.sort()
})

const availableTypes = computed(() => {
  const types = [...new Set(galleryItems.value.map(item => item.type).filter(type => type))]
  return types.sort()
})

const hasActiveFilters = computed(() => {
  return selectedType.value !== 'all' || selectedAlbum.value !== 'all'
})

// Methods
const fetchGalleryData = async () => {
  if (!props.clusterId) {
    console.warn('No cluster ID provided for gallery')
    error.value = 'ไม่พบรหัสคลัสเตอร์'
    isLoading.value = false
    return
  }

  isLoading.value = true
  error.value = null

  try {
    console.log('Fetching gallery data for cluster:', props.clusterId)
    
    const response = await $DSS('cms/media', 'GET', null, { clusterId: props.clusterId })
    
    if (response && response.success) {
      let mediaData = response.data || []
      
      // Filter only images and videos for gallery display
      mediaData = mediaData.filter(item => 
        item.type === 'image' || item.type === 'video' || item.type === 'document'
      )
      
      // Sort by creation date (newest first)
      mediaData.sort((a, b) => {
        const dateA = new Date(a.createdAt || a.created_at || 0)
        const dateB = new Date(b.createdAt || b.created_at || 0)
        return dateB - dateA
      })
      
      galleryItems.value = mediaData
      console.log('Gallery loaded:', galleryItems.value.length, 'items')
    } else {
      throw new Error(response?.error || 'Failed to fetch gallery data')
    }
  } catch (err) {
    console.error('Error fetching gallery:', err)
    error.value = err.message || 'ไม่สามารถโหลดแกลเลอรี่ได้'
    galleryItems.value = []
  } finally {
    isLoading.value = false
  }
}

const getFileExtension = (filename) => {
  if (!filename) return ''
  const parts = filename.split('.')
  return parts.length > 1 ? parts.pop().toUpperCase() : ''
}

const getTypeCount = (type) => {
  return galleryItems.value.filter(item => item.type === type).length
}

const getAlbumCount = (album) => {
  return galleryItems.value.filter(item => item.album === album).length
}

const getTypeLabel = (type) => {
  switch (type) {
    case 'image': return 'รูปภาพ'
    case 'video': return 'วีดีโอ'
    case 'document': return 'เอกสาร'
    default: return 'ไฟล์'
  }
}

const clearFilters = () => {
  selectedType.value = 'all'
  selectedAlbum.value = 'all'
}

const handleImageError = (event) => {
  event.target.style.display = 'none';
  event.target.parentNode.innerHTML = '<div class="flex items-center justify-center h-full"><i class="fas fa-image text-gray-400 text-2xl"></i></div>';
}



// Lightbox methods
const openLightbox = (item) => {
  const index = filteredItems.value.findIndex(i => (i._id || i.id) === (item._id || item.id))
  lightbox.value = {
    show: true,
    item: item,
    currentIndex: index
  }
  
  // Prevent body scroll when lightbox is open
  document.body.style.overflow = 'hidden'
}

const closeLightbox = () => {
  lightbox.value = {
    show: false,
    item: null,
    currentIndex: 0
  }
  
  // Restore body scroll
  document.body.style.overflow = ''
}

const nextImage = () => {
  if (lightbox.value.currentIndex < filteredItems.value.length - 1) {
    lightbox.value.currentIndex++
    lightbox.value.item = filteredItems.value[lightbox.value.currentIndex]
  }
}

const previousImage = () => {
  if (lightbox.value.currentIndex > 0) {
    lightbox.value.currentIndex--
    lightbox.value.item = filteredItems.value[lightbox.value.currentIndex]
  }
}

// Keyboard navigation for lightbox
const handleKeydown = (event) => {
  if (lightbox.value.show) {
    switch (event.key) {
      case 'Escape':
        closeLightbox()
        break
      case 'ArrowRight':
        nextImage()
        break
      case 'ArrowLeft':
        previousImage()
        break
    }
  }
}

// Lifecycle
onMounted(async () => {
  await fetchGalleryData()
  
  // Add keyboard event listener
  document.addEventListener('keydown', handleKeydown)
})

// Cleanup
onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
  // Restore body scroll if component is unmounted while lightbox is open
  document.body.style.overflow = ''
})

// Watch for clusterId changes
watch(() => props.clusterId, (newClusterId) => {
  if (newClusterId) {
    fetchGalleryData()
  }
}, { immediate: false })
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Lightbox overlay */
.lightbox-overlay {
  backdrop-filter: blur(4px);
}

/* Custom scrollbar for modal content */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>